import { Results, SearchTabs } from "@/components/search/search-layout"
import { SearchWorkspace } from "@/components/search/search-workspace"
import { ClearSearchFacets } from "@/components/search/clear-search-facets"
import { EducatorProfileFacet } from "@/components/search/educator-profile-facet"
import { SearchProvider } from "@/components/search/search-provider"
import { useTermFacetDescriptor } from "@/components/search/facet-descriptor"
import { SidebarFacet } from "@/components/search/sidebar-facet"
import { FullTextFilter } from "@/components/search/full-text-filter"
import { Card, CardHeader, CardTitle } from "@/components/ui/card"
import { SearchUrlState } from "@/lib/redux-search/search-url-state"
import type {
  SearchReliefListingsEducatorDocument,
  SearchReliefListingsLearnerDocument,
} from "@/types/serializers"
import { SearchDetailLink } from "@/components/search/search-return-navigation"
import { useTranslation } from "react-i18next"
import { FACET_IDS } from "@/components/search/facet-ids"

export default function Page() {
  const { t } = useTranslation("search")
  return (
    <ReliefListingSearchProvider>
      <SearchUrlState />
      <SearchWorkspace>
        <SearchWorkspace.Sidebar>
          <ReliefListingSearchFacets />
        </SearchWorkspace.Sidebar>
        <SearchWorkspace.Content>
          <SearchTabs />
          <FullTextFilter filterId="title" fields={["title"]} />
          <Results
            empty={
              <div className="flex h-full flex-col items-center justify-center">
                <h2 className="mb-2 text-2xl font-bold tracking-tight">
                  {t("result.relief_listings.empty.label")}
                </h2>
                <p className="mb-8 max-w-md">
                  {t("result.relief_listings.empty.description")}
                </p>
              </div>
            }
            ResultComponent={Result}
          />
        </SearchWorkspace.Content>
      </SearchWorkspace>
    </ReliefListingSearchProvider>
  )
}

function ReliefListingSearchProvider({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <SearchProvider
      source={[
        "title",
        "listing_type",
        "subject_key",
        "curriculum_key",
        "delivery_mode",
        "result.title",
        "tags",
      ]}
      url="/search/gateway/relief_listings/query"
    >
      {children}
    </SearchProvider>
  )
}

type ReliefListingSearchHit = { id: string } & (
  | SearchReliefListingsEducatorDocument
  | SearchReliefListingsLearnerDocument
)

function Result({ result }: { result: ReliefListingSearchHit }) {
  return (
    <li>
      <SearchDetailLink
        href={`/search/relief_listings/${result.id}`}
        aria-label={result.result.title}
        role="link"
      >
        <Card hoverable className="group">
          <CardHeader>
            <CardTitle>{result.result.title}</CardTitle>
          </CardHeader>
        </Card>
      </SearchDetailLink>
    </li>
  )
}

function ReliefListingSearchFacets() {
  return (
    <>
      <ClearSearchFacets />
      <ListingTypeFacet />
      <CurriculumFacet />
      <SubjectFacet />
      <DeliveryModeFacet />
      <EducatorProfileFacet id={FACET_IDS.tags} field="tags.keyword" />
    </>
  )
}

function ListingTypeFacet() {
  const { t } = useTranslation("search")
  const { t: tFacet } = useTranslation("search", {
    keyPrefix: "relief_listings.listing_type",
  })
  const label = t("facet.listing_type")
  const pillLabel = (value: string) => `${label} ${tFacet(value as never)}`
  const descriptor = useTermFacetDescriptor({
    id: FACET_IDS.listingType,
    config: { type: "terms", size: 1000, field: "listing_type.keyword" },
    label,
    pillLabel,
    option: (value, count) => ({
      value,
      count,
      label: tFacet(value as never),
    }),
  })

  return <SidebarFacet descriptor={descriptor} />
}

function CurriculumFacet() {
  const { t } = useTranslation("search")
  const { t: tFacet } = useTranslation("subjects", { keyPrefix: "curricula" })
  const label = t("facet.curriculum")
  const pillLabel = (value: string) =>
    `${t("result.curriculum")} ${tFacet(value as never)}`
  const descriptor = useTermFacetDescriptor({
    id: FACET_IDS.curriculum,
    config: { type: "terms", size: 1000, field: "curriculum_key" },
    label,
    pillLabel,
    searchable: true,
    option: (value, count) => ({
      value,
      count,
      label: tFacet(value as never),
    }),
  })

  return <SidebarFacet descriptor={descriptor} />
}

function SubjectFacet() {
  const { t } = useTranslation("search")
  const { t: tFacet } = useTranslation("subjects", { keyPrefix: "subjects" })
  const label = t("facet.subject")
  const pillLabel = (value: string) =>
    `${t("result.subject")} ${tFacet(value as never)}`
  const descriptor = useTermFacetDescriptor({
    id: FACET_IDS.subject,
    config: { type: "terms", size: 1000, field: "subject_key" },
    label,
    pillLabel,
    searchable: true,
    option: (value, count) => ({
      value,
      count,
      label: tFacet(value as never),
    }),
  })

  return <SidebarFacet descriptor={descriptor} />
}

function DeliveryModeFacet() {
  const { t } = useTranslation("search")
  const { t: tFacet } = useTranslation("search", {
    keyPrefix: "relief_listings.delivery_mode",
  })
  const label = t("facet.delivery_mode")
  const pillLabel = (value: string) => `${label} ${tFacet(value as never)}`
  const descriptor = useTermFacetDescriptor({
    id: FACET_IDS.deliveryMode,
    config: { type: "terms", size: 1000, field: "delivery_mode.keyword" },
    label,
    pillLabel,
    option: (value, count) => ({
      value,
      count,
      label: tFacet(value as never),
    }),
  })

  return <SidebarFacet descriptor={descriptor} />
}
